import type { PlaceTag } from "@mm/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { api, fieldError } from "@/lib/api";
import { fromLocalInput, toLocalInput } from "@/lib/dates";
import { Icon } from "@/ui/icons";
import { Badge, Button, Card, cx, Empty, ErrorText, Field, ImageUpload, Input, Section, Select, Textarea, Toggle } from "@/ui/kit";
import { PageHeader } from "@/ui/Shell";

interface Row { id: string; title: string; address: string; status: string; tagIds: number[]; sponsoredUntil: string | null }
const useTags = () => useQuery({ queryKey: ["admin-tags"], queryFn: () => api<(PlaceTag & { sortOrder: number })[]>("/v1/admin/place-tags") });

export function Places() {
  const nav = useNavigate();
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const r = useQuery({ queryKey: ["admin-places", status, q], queryFn: () => api<Row[]>(`/v1/admin/places?status=${status}${q ? `&q=${encodeURIComponent(q)}` : ""}`) });
  const tags = new Map((useTags().data ?? []).map((t) => [t.id, t]));
  return (
    <>
      <PageHeader title="Map places" back="/more" action={<Button onClick={() => nav("/places/new")} className="!px-4"><Icon name="plus" className="size-5" />New</Button>} />
      <Input placeholder="Search places" value={q} onChange={(e) => setQ(e.target.value)} className="mb-2" aria-label="Search places" />
      <div className="mb-3 flex gap-1.5 overflow-x-auto">
        {["all", "published", "draft", "archived"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={cx("min-h-10 rounded-full border px-4 text-sm capitalize", status === s ? "border-ink bg-ink text-white" : "border-line bg-white")}>{s}</button>
        ))}
      </div>
      <ErrorText error={r.error} />
      <div className="grid gap-2">
        {(r.data ?? []).map((p) => (
          <Card key={p.id} onClick={() => nav(`/places/${p.id}`)}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-semibold">{p.title}</p>
                <p className="truncate text-sm text-muted">{p.tagIds.map((t) => tags.get(t)?.name).filter(Boolean).join(", ") || "No category"}</p>
              </div>
              <div className="flex shrink-0 gap-1">
                {p.sponsoredUntil && Date.parse(p.sponsoredUntil) > Date.now() ? <Badge tone="butter">Sponsored</Badge> : null}
                {p.status !== "published" ? <Badge tone={p.status === "draft" ? "blush" : "mist"}>{p.status}</Badge> : null}
              </div>
            </div>
          </Card>
        ))}
      </div>
      {r.isSuccess && !r.data.length ? <Empty title="No places match" /> : null}
    </>
  );
}

const AGE_OPTIONS = [0, 6, 12, 24, 36, 48, 60, 72, 96, 120, 144, 216];
const ageLabel = (m: number) => (m === 0 ? "Newborn" : m === 216 ? "18+" : m < 24 ? `${m} months` : `${m / 12} years`);

export function PlaceEdit() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const nav = useNavigate();
  const qc = useQueryClient();
  const tags = useTags().data ?? [];
  const q = useQuery({ queryKey: ["admin-place", id], queryFn: () => api<Record<string, unknown>>(`/v1/admin/places/${id}`), enabled: !isNew });
  const [f, setF] = useState({
    title: "", description: "", address: "", lat: null as number | null, lng: null as number | null, tagIds: [] as number[], minAgeMonths: 0, maxAgeMonths: 216,
    website: "", phone: "", highlights: "", imageId: null as string | null, imageUrl: null as string | null, sponsored: false, sponsoredUntil: "", status: "published",
  });
  const [found, setFound] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const p = q.data;

  useEffect(() => {
    if (!p) return;
    const su = p.sponsoredUntil as string | null;
    setF({
      title: String(p.title), description: String(p.description ?? ""), address: String(p.address), lat: p.lat as number, lng: p.lng as number, tagIds: p.tagIds as number[],
      minAgeMonths: p.minAgeMonths as number, maxAgeMonths: p.maxAgeMonths as number, website: String(p.website ?? ""), phone: String(p.phone ?? ""),
      highlights: ((p.highlights as string[]) ?? []).join(", "), imageId: (p.imageKey as string) ?? null, imageUrl: (p.imageUrl as string) ?? null,
      sponsored: !!su && Date.parse(su) > Date.now(), sponsoredUntil: su ? toLocalInput(su) : toLocalInput(new Date(Date.now() + 30 * 86_400_000)), status: String(p.status),
    });
    setFound(`Pin at ${(p.lat as number).toFixed(5)}, ${(p.lng as number).toFixed(5)}`);
  }, [p]);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));

  async function save() {
    setBusy("save");
    setError(null);
    try {
      const body = {
        title: f.title, description: f.description, address: f.address, lat: f.lat, lng: f.lng, tagIds: f.tagIds, minAgeMonths: f.minAgeMonths, maxAgeMonths: f.maxAgeMonths,
        website: f.website || null, phone: f.phone || null, highlights: f.highlights.split(",").map((h) => h.trim()).filter(Boolean).slice(0, 8), imageAttachmentId: f.imageId,
        sponsoredUntil: f.sponsored && f.sponsoredUntil ? fromLocalInput(f.sponsoredUntil) : null, status: f.status,
      };
      const r = isNew ? await api<{ id: string }>("/v1/admin/places", { method: "POST", body }) : await api<{ id: string }>(`/v1/admin/places/${id}`, { method: "PUT", body });
      await qc.invalidateQueries({ queryKey: ["admin-places"] });
      await qc.invalidateQueries({ queryKey: ["admin-place", id] });
      if (isNew) nav(`/places/${r.id}`, { replace: true });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <PageHeader title={isNew ? "New place" : f.title || "Place"} back="/places" />
      <ImageUpload url={f.imageUrl} onUploaded={(aid, url) => setF((x) => ({ ...x, imageId: aid, imageUrl: url }))} onClear={() => setF((x) => ({ ...x, imageId: null, imageUrl: null }))} />
      <div className="h-4" />
      <Field label="Name">{(i) => <Input id={i} value={f.title} onChange={(e) => set("title", e.target.value)} />}</Field>
      <Field label="Address" hint={found ?? "We'll find it on the map when you save."} error={error && (error as { code?: string }).code === "geocode_failed" ? (error as Error).message : null}>
        {(i) => (
          <div className="flex gap-2">
            <Input id={i} value={f.address} onChange={(e) => { set("address", e.target.value); setF((x) => ({ ...x, lat: null, lng: null })); setFound(null); }} />
            <Button type="button" variant="secondary" loading={busy === "geo"} onClick={async () => {
              setBusy("geo");
              setError(null);
              try {
                const g = await api<{ lat: number; lng: number; formatted: string }>("/v1/admin/geocode", { method: "POST", body: { address: f.address } });
                setF((x) => ({ ...x, lat: g.lat, lng: g.lng }));
                setFound(`📍 ${g.formatted}`);
              } catch (e) {
                setError(e);
              } finally {
                setBusy(null);
              }
            }}>Check</Button>
          </div>
        )}
      </Field>
      <Field label="Description">{(i) => <Textarea id={i} value={f.description} onChange={(e) => set("description", e.target.value)} />}</Field>
      <Section title="Categories">
        <div className="flex flex-wrap gap-2">
          {tags.map((t) => {
            const on = f.tagIds.includes(t.id);
            return <button type="button" key={t.id} onClick={() => set("tagIds", on ? f.tagIds.filter((x) => x !== t.id) : [...f.tagIds, t.id])} className={cx("min-h-10 rounded-full border px-4 text-sm font-semibold", on ? "border-ink bg-ink text-white" : "border-line bg-white")}>{t.name}</button>;
          })}
        </div>
        {fieldError(error, "tagIds") ? <p className="mt-1 text-sm text-danger">{fieldError(error, "tagIds")}</p> : null}
      </Section>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Youngest">{(i) => <Select id={i} value={f.minAgeMonths} onChange={(e) => set("minAgeMonths", Number(e.target.value))}>{AGE_OPTIONS.slice(0, -1).map((m) => <option key={m} value={m}>{ageLabel(m)}</option>)}</Select>}</Field>
        <Field label="Oldest">{(i) => <Select id={i} value={f.maxAgeMonths} onChange={(e) => set("maxAgeMonths", Number(e.target.value))}>{AGE_OPTIONS.slice(1).map((m) => <option key={m} value={m}>{ageLabel(m)}</option>)}</Select>}</Field>
      </div>
      <Field label="Highlights" hint="Comma separated, e.g. Fenced, Restrooms, Free">{(i) => <Input id={i} value={f.highlights} onChange={(e) => set("highlights", e.target.value)} />}</Field>
      <div className="grid gap-x-2 sm:grid-cols-2">
        <Field label="Website" error={fieldError(error, "website")}>{(i) => <Input id={i} type="url" inputMode="url" value={f.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" />}</Field>
        <Field label="Phone">{(i) => <Input id={i} type="tel" value={f.phone} onChange={(e) => set("phone", e.target.value)} />}</Field>
      </div>
      <Section title="Sponsorship">
        <Toggle label="Sponsored" hint="Bigger starred pin, labeled “Sponsored”, top of nearby lists." checked={f.sponsored} onChange={(v) => set("sponsored", v)} />
        {f.sponsored ? <Field label="Sponsored until">{(i) => <Input id={i} type="datetime-local" value={f.sponsoredUntil} onChange={(e) => set("sponsoredUntil", e.target.value)} />}</Field> : null}
      </Section>
      <Field label="Status">
        {(i) => (
          <Select id={i} value={f.status} onChange={(e) => set("status", e.target.value)}>
            <option value="published">Published (on the map)</option>
            <option value="draft">Draft (hidden)</option>
            <option value="archived">Archived (removed)</option>
          </Select>
        )}
      </Field>
      <ErrorText error={error} />
      <div className="pb-safe sticky bottom-20 z-10 -mx-4 bg-cream/95 px-4 py-3 backdrop-blur lg:bottom-0">
        <Button className="w-full" loading={busy === "save"} onClick={() => void save()}>Save place</Button>
      </div>
    </>
  );
}

export function Categories() {
  const qc = useQueryClient();
  const tags = useTags();
  const [name, setName] = useState("");
  const [color, setColor] = useState("#D1E2DF");
  const [error, setError] = useState<unknown>(null);
  return (
    <>
      <PageHeader title="Map categories" back="/more" />
      <div className="mb-4 grid gap-2">
        {(tags.data ?? []).map((t) => (
          <Card key={t.id} className="flex items-center gap-3">
            <span className="size-6 rounded-full ring-2 ring-white" style={{ background: t.color }} />
            <span className="font-semibold">{t.name}</span>
          </Card>
        ))}
      </div>
      <Card>
        <p className="mb-2 font-semibold">Add a category</p>
        <div className="grid grid-cols-[1fr_4rem] gap-2">
          <Input placeholder="e.g. Swim" value={name} onChange={(e) => setName(e.target.value)} aria-label="Category name" />
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-12 w-full rounded-xl border border-line" aria-label="Pin color" />
        </div>
        <ErrorText error={error} />
        <Button className="mt-2 w-full" disabled={name.trim().length < 2} onClick={async () => {
          setError(null);
          try {
            await api("/v1/admin/place-tags", { method: "POST", body: { name: name.trim(), color: color.toUpperCase(), icon: "star" } });
            setName("");
            await qc.invalidateQueries({ queryKey: ["admin-tags"] });
          } catch (e) {
            setError(e);
          }
        }}>Add category</Button>
      </Card>
    </>
  );
}
